import { Box, Button, Icon } from '@palmetto/palmetto-components';
// oxlint-disable-next-line
import pdfWorkerURL from 'pdfjs-dist/build/pdf.worker.min?url';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import { BoundingBox, BoundingBoxCoordinateType, DocumentLayout } from 'types';

interface PdfViewerProps {
  url: string;
  fileName: string;
  contentType?: 'pdf' | 'image';
  documentLayout?: DocumentLayout;
  style?: React.CSSProperties;
  onImageLoad?: () => void;
  onImageError?: () => void;
  /** 1-based page number this image corresponds to in documentLayout when it's one file of a multi-file document; ignored for contentType === 'pdf' */
  imagePageNumber?: number;
}

export const PdfViewer: React.FC<PdfViewerProps> = ({
  url,
  fileName,
  contentType = 'pdf',
  documentLayout,
  style,
  onImageLoad,
  onImageError,
  imagePageNumber,
}) => {
  const [numPages, setNumPages] = useState<number | null>(contentType === 'image' ? 1 : null);
  const [pageNumber, setPageNumber] = useState(contentType === 'image' ? (imagePageNumber ?? 1) : 1);
  const [scale, setScale] = useState(1.0);
  const [pageWidth, setPageWidth] = useState<number>(0);
  const [pageHeight, setPageHeight] = useState<number>(0);
  const [showBoundingBoxes, setShowBoundingBoxes] = useState(true);
  const [showLabels, setShowLabels] = useState(true);
  const [hasAutoScaled, setHasAutoScaled] = useState(false);
  const [hoveredBox, setHoveredBox] = useState<BoundingBox | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    try {
      if (typeof window === 'undefined' || !('Worker' in window)) {
        throw new Error('Web Workers not supported in this environment.');
      }
      pdfjs.GlobalWorkerOptions.workerSrc = new URL(pdfWorkerURL, import.meta.url).toString();
    } catch (error) {
      console.error(`Unable to load pdfjs worker: ${error}`);
    }
  }, []);

  useEffect(() => {
    if (contentType === 'image' && imagePageNumber !== undefined) {
      setPageNumber(imagePageNumber);
    }
  }, [contentType, imagePageNumber]);

  const onDocumentLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setNumPages(numPages);
    },
    [setNumPages],
  );

  const onPageLoadSuccess = useCallback(
    async (page: any) => {
      const { width, height } = page;
      setPageWidth(width);
      setPageHeight(height);
    },
    [setPageWidth, setPageHeight],
  );

  const handleImageLoad = useCallback(() => {
    const img = imageRef.current;
    if (img) {
      setPageWidth(img.naturalWidth);
      setPageHeight(img.naturalHeight);
    }
    onImageLoad?.();
  }, [onImageLoad]);

  const handleImageError = useCallback(() => {
    onImageError?.();
  }, [onImageError]);

  // The rendered element the overlay is measured against: react-pdf's page canvas, or the image
  const getRenderedElement = useCallback((): HTMLElement | null => {
    if (contentType === 'image') {
      return imageRef.current;
    }
    return pageRef.current?.querySelector('.react-pdf__Page canvas') ?? null;
  }, [contentType]);

  // Calculate scaling factors for converting DocumentAI coordinates to displayed coordinates
  const calculateScaleFactors = useCallback(() => {
    const pageElement = getRenderedElement();

    if (!pageElement) {
      return null;
    }

    const pageElementRect = pageElement.getBoundingClientRect();

    // Get the actual rendered page element dimensions
    const displayedPageWidth = pageElementRect.width;
    const displayedPageHeight = pageElementRect.height;

    // Pixel coordinates need the DocumentAI page dimensions to compute a scale factor;
    // normalized coordinates only need the displayed element size, so leave these undefined
    // when page dimensions aren't available rather than bailing out entirely.
    let totalScaleX: number | undefined;
    let totalScaleY: number | undefined;

    const currentPageInfo = documentLayout?.pages?.find((p) => p.pageNumber === pageNumber);
    if (currentPageInfo && pageWidth && pageHeight) {
      const { width: originalPageWidth, height: originalPageHeight } = currentPageInfo;

      // Scale from DocumentAI coordinates to PDF intrinsic coordinates
      const docAiToPdfScaleX = pageWidth / originalPageWidth;
      const docAiToPdfScaleY = pageHeight / originalPageHeight;

      // Then scale from PDF intrinsic coordinates to displayed coordinates (includes zoom scale)
      const pdfToDisplayScaleX = displayedPageWidth / (pageWidth * scale);
      const pdfToDisplayScaleY = displayedPageHeight / (pageHeight * scale);

      // Combined scale factor: DocumentAI -> PDF intrinsic -> displayed
      totalScaleX = docAiToPdfScaleX * pdfToDisplayScaleX * scale;
      totalScaleY = docAiToPdfScaleY * pdfToDisplayScaleY * scale;
    }

    return {
      totalScaleX,
      totalScaleY,
      displayedPageWidth,
      displayedPageHeight,
      pageElementRect,
    };
  }, [getRenderedElement, documentLayout, pageNumber, pageWidth, pageHeight, scale]);

  // Convert bounding box coordinates to display coordinates
  // Supports both pixel and normalized (0-1 range) coordinate types
  const convertBoundingBoxToDisplayCoordinates = useCallback(
    (box: BoundingBox, scaleFactors: ReturnType<typeof calculateScaleFactors>) => {
      if (!scaleFactors) return null;

      const pageElement = getRenderedElement();
      if (!pageElement) return null;

      // Get the CSS display dimensions (not the internal canvas resolution)
      const pageElementRect = pageElement.getBoundingClientRect();
      const displayWidth = pageElementRect.width;
      const displayHeight = pageElementRect.height;

      const [x1, y1, x2, y2] = box.boundingBox;

      let topLeftX: number;
      let topLeftY: number;
      let bottomRightX: number;
      let bottomRightY: number;

      // Handle both coordinate types
      if (box.coordinateType === BoundingBoxCoordinateType.normalized) {
        // Normalized coordinates (0-1 range) - convert to display coordinates
        // Use the CSS display dimensions
        topLeftX = x1 * displayWidth;
        topLeftY = y1 * displayHeight;
        bottomRightX = x2 * displayWidth;
        bottomRightY = y2 * displayHeight;
      } else {
        // Pixel coordinates need a scale factor derived from documentLayout.pages; bail per-box
        // when it's absent rather than rendering with garbage coordinates.
        const { totalScaleX, totalScaleY } = scaleFactors;
        if (totalScaleX === undefined || totalScaleY === undefined) {
          return null;
        }
        topLeftX = x1 * totalScaleX;
        topLeftY = y1 * totalScaleY;
        bottomRightX = x2 * totalScaleX;
        bottomRightY = y2 * totalScaleY;
      }

      // Ensure coordinates are properly ordered (top-left to bottom-right)
      const minX = Math.min(topLeftX, bottomRightX);
      const minY = Math.min(topLeftY, bottomRightY);
      const maxX = Math.max(topLeftX, bottomRightX);
      const maxY = Math.max(topLeftY, bottomRightY);

      return {
        minX,
        minY,
        maxX,
        maxY,
        width: maxX - minX,
        height: maxY - minY,
      };
    },
    [getRenderedElement],
  );

  // Draw bounding boxes on canvas overlay
  const drawBoundingBoxes = useCallback(() => {
    const canvas = canvasRef.current;
    const pageContainer = pageRef.current;
    if (!canvas || !pageContainer || !documentLayout?.boundingBoxes) {
      return;
    }

    const scaleFactors = calculateScaleFactors();
    if (!scaleFactors) {
      return;
    }

    const { displayedPageWidth, displayedPageHeight } = scaleFactors;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Size the canvas to match the ACTUAL PAGE ELEMENT size exactly
    // This ensures the canvas overlay perfectly aligns with the rendered PDF
    canvas.width = displayedPageWidth;
    canvas.height = displayedPageHeight;
    canvas.style.width = `${displayedPageWidth}px`;
    canvas.style.height = `${displayedPageHeight}px`;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Filter bounding boxes for current page
    const currentPageBoxes = documentLayout.boundingBoxes.filter((box) => box.pageNumber === pageNumber);

    currentPageBoxes.forEach((box) => {
      const coords = convertBoundingBoxToDisplayCoordinates(box, scaleFactors);
      if (!coords) {
        return;
      }

      const { minX, minY, width, height } = coords;

      // Draw bounding box with padding to prevent border from touching text
      const borderWidth = 1.5;
      const padding = borderWidth; // Add padding equal to border width
      ctx.strokeStyle = 'rgba(255, 68, 68, 0.8)';
      ctx.lineWidth = borderWidth;
      ctx.strokeRect(minX - padding, minY - padding, width + padding * 2, height + padding * 2);

      if (showLabels) {
        // Draw label background
        const labelPadding = 6;
        const labelHeight = 20;

        // Set font before measuring text
        ctx.font = '12px sans-serif';

        // Measure the actual text width to ensure label background is wide enough
        const textMetrics = ctx.measureText(box.fieldName);
        const textWidth = textMetrics.width;
        const labelWidth = textWidth + labelPadding * 2; // Add padding on both sides

        // Draw label background with width based on text content
        ctx.fillStyle = 'rgba(50,50,50,0.6)';
        ctx.fillRect(minX, minY - labelHeight, labelWidth, labelHeight);

        // Draw label text
        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'left';
        ctx.fillText(box.fieldName, minX + labelPadding, minY - labelPadding);
      }
    });
  }, [
    documentLayout,
    pageNumber,
    showLabels,
    calculateScaleFactors,
    convertBoundingBoxToDisplayCoordinates,
    scale,
    pageWidth,
    pageHeight,
  ]);

  // Redraw bounding boxes when dependencies change
  useEffect(() => {
    if (showBoundingBoxes) {
      // Add a small delay to ensure the page has fully rendered
      const timer = setTimeout(() => {
        drawBoundingBoxes();
      }, 50);
      return () => clearTimeout(timer);
    } else {
      // Clear the canvas when bounding boxes are hidden
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
      }
    }
  }, [drawBoundingBoxes, showBoundingBoxes, pageWidth, pageHeight, scale]);

  const goToPrevPage = () => {
    setPageNumber((prev) => Math.max(1, prev - 1));
  };

  const goToNextPage = () => {
    setPageNumber((prev) => Math.min(numPages || 1, prev + 1));
  };

  const zoomIn = () => {
    setScale((prev) => Math.min(3.0, prev + 0.2));
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(0.5, prev - 0.2));
  };

  const resetZoom = () => {
    // Reset to fit-to-height scale instead of 1.0
    if (pageHeight > 0) {
      const toolbarHeight = 60;
      const padding = 32;
      const availableHeight = window.innerHeight - toolbarHeight - padding;
      const fitScale = Math.min(availableHeight / pageHeight, 2.0);
      setScale(fitScale);
    } else {
      setScale(1.0);
    }
  };

  const toggleBoundingBoxes = () => {
    setShowBoundingBoxes((prev) => !prev);
  };

  const toggleLabels = () => {
    setShowLabels((prev) => !prev);
  };

  // Auto-scale to fit PDF to available vertical space
  useEffect(() => {
    if (!pageHeight || hasAutoScaled) return;

    const handleAutoScale = () => {
      const toolbarHeight = 60;
      const padding = 32;
      const availableHeight = window.innerHeight - toolbarHeight - padding;

      if (availableHeight > 0) {
        const newScale = Math.min(availableHeight / pageHeight, 2.0);
        setScale(newScale);
        setHasAutoScaled(true);
      }
    };

    // Use a small delay to ensure the container is properly sized
    const timer = setTimeout(() => {
      handleAutoScale();
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [pageHeight, hasAutoScaled]);

  // Handle window resize separately to re-auto-scale
  useEffect(() => {
    const handleResize = () => {
      if (!pageHeight) return;

      const toolbarHeight = 60;
      const padding = 32;
      const availableHeight = window.innerHeight - toolbarHeight - padding;

      if (availableHeight > 0) {
        const newScale = Math.min(availableHeight / pageHeight, 2.0);
        setScale(newScale);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [pageHeight]);

  // Update mouse position and hovered box
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const { clientX, clientY } = e;

    if (showBoundingBoxes && documentLayout?.boundingBoxes) {
      const pageContainer = pageRef.current;
      if (!pageContainer) return;

      const scaleFactors = calculateScaleFactors();
      if (!scaleFactors) return;

      const { pageElementRect } = scaleFactors;

      const containerRect = pageContainer.getBoundingClientRect();

      const relativeX = clientX - containerRect.left;
      const relativeY = clientY - containerRect.top;

      // Calculate offset of the page element within the container
      const pageOffsetX = pageElementRect.left - containerRect.left;
      const pageOffsetY = pageElementRect.top - containerRect.top;

      // Find the bounding box under the mouse cursor
      const hovered = documentLayout.boundingBoxes.find((box) => {
        if (box.pageNumber !== pageNumber) return false;

        const coords = convertBoundingBoxToDisplayCoordinates(box, scaleFactors);
        if (!coords) return false;

        const { minX, minY, maxX, maxY } = coords;

        // Adjust for page offset within container
        const adjustedMinX = minX + pageOffsetX;
        const adjustedMinY = minY + pageOffsetY;
        const adjustedMaxX = maxX + pageOffsetX;
        const adjustedMaxY = maxY + pageOffsetY;

        // Check if the mouse is over this bounding box (using container-relative coordinates)
        return (
          relativeX >= adjustedMinX &&
          relativeX <= adjustedMaxX &&
          relativeY >= adjustedMinY &&
          relativeY <= adjustedMaxY
        );
      });

      setHoveredBox(hovered || null);
    }
  };

  // Calculate tooltip position for hovered bounding box
  const getTooltipPosition = () => {
    if (!hoveredBox) return { top: 0, left: 0, width: 0 };

    const pageContainer = pageRef.current;
    if (!pageContainer) return { top: 0, left: 0, width: 0 };

    const scaleFactors = calculateScaleFactors();
    if (!scaleFactors) return { top: 0, left: 0, width: 0 };

    const { pageElementRect } = scaleFactors;

    const containerRect = pageContainer.getBoundingClientRect();

    // Calculate offset of the page element within the container
    const pageOffsetX = pageElementRect.left - containerRect.left;
    const pageOffsetY = pageElementRect.top - containerRect.top;

    const coords = convertBoundingBoxToDisplayCoordinates(hoveredBox, scaleFactors);
    if (!coords) return { top: 0, left: 0, width: 0 };

    const { minX, minY } = coords;

    // Adjust for page offset within container
    const adjustedMinX = minX + pageOffsetX;
    const adjustedMinY = minY + pageOffsetY;

    // Calculate tooltip width based on text content
    // Create a temporary canvas to measure text width
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.font = '12px sans-serif';
      const textMetrics = ctx.measureText(hoveredBox.fieldName);
      const labelPadding = 6;
      const tooltipWidth = textMetrics.width + labelPadding * 2;

      const labelHeight = 20;
      const tooltipTop = adjustedMinY - labelHeight;
      const tooltipLeft = adjustedMinX;

      return { top: tooltipTop, left: tooltipLeft, width: tooltipWidth };
    }

    // Fallback if context not available
    const labelHeight = 20;
    const tooltipTop = adjustedMinY - labelHeight;
    const tooltipLeft = adjustedMinX;
    const tooltipWidth = 100; // Default fallback width

    return { top: tooltipTop, left: tooltipLeft, width: tooltipWidth };
  };

  return (
    <Box direction="column" style={{ ...style, height: '100vh' }}>
      <Box
        direction="row"
        gap="sm"
        padding="xs"
        background="grey-900"
        alignItems="center"
        style={{
          borderBottom: '1px solid #e5e5e5',
          flexShrink: 0,
          zIndex: 30,
        }}
      >
        {contentType === 'pdf' && (
          <>
            <Button
              size="sm"
              variant="secondary"
              onClick={goToPrevPage}
              disabled={pageNumber <= 1}
              iconPrefix="caret-left"
            />
            <Box fontWeight="medium" color="grey-100">
              Page {pageNumber} of {numPages || 0}
            </Box>
            <Button
              size="sm"
              variant="secondary"
              onClick={goToNextPage}
              disabled={pageNumber >= (numPages || 0)}
              iconPrefix="caret-right"
            />
          </>
        )}

        <Box direction="row" gap="xs" margin="0 0 0 md">
          <Button size="sm" variant="secondary" onClick={zoomOut} iconPrefix="zoom-out" />
          <Button size="sm" variant="secondary" onClick={resetZoom}>
            {Math.round(scale * 100)}%
          </Button>
          <Button size="sm" variant="secondary" onClick={zoomIn} iconPrefix="zoom-in" />
        </Box>

        {documentLayout?.boundingBoxes && documentLayout.boundingBoxes.length > 0 && (
          <Box direction="row" alignItems="center" gap="xs" margin="0 0 0 md">
            <Icon name="antenna" color="danger-500" size="sm" />
            <Box fontSize="sm" color="grey-200">
              {documentLayout.boundingBoxes.filter((box) => box.pageNumber === pageNumber).length} field(s) detected
            </Box>
          </Box>
        )}

        {documentLayout?.boundingBoxes && documentLayout.boundingBoxes.length > 0 && (
          <Button
            size="sm"
            variant="secondary"
            onClick={toggleBoundingBoxes}
            iconPrefix={showBoundingBoxes ? 'eye-slash' : 'eye'}
          >
            {showBoundingBoxes ? 'Hide' : 'Show'} Bounding Boxes
          </Button>
        )}

        {documentLayout?.boundingBoxes && documentLayout.boundingBoxes.length > 0 && (
          <Button
            size="sm"
            variant="secondary"
            onClick={toggleLabels}
            iconPrefix={showLabels ? 't-warning' : 'tag'}
            disabled={!showBoundingBoxes}
          >
            {showLabels ? 'Hide' : 'Show'} Labels
          </Button>
        )}
      </Box>

      <Box
        ref={containerRef}
        style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          padding: 'sm',
          overflow: 'auto',
          minHeight: 0,
        }}
        onMouseMove={handleMouseMove}
      >
        <Box>
          <Box ref={pageRef} style={{ position: 'relative' }}>
            {contentType === 'image' ? (
              <img
                ref={imageRef}
                src={url}
                alt={fileName}
                onLoad={handleImageLoad}
                onError={handleImageError}
                style={{
                  display: 'block',
                  width: pageWidth ? `${pageWidth * scale}px` : undefined,
                  height: 'auto',
                }}
              />
            ) : (
              <Document
                file={url}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={
                  <Box padding="lg" textAlign="center">
                    Loading PDF...
                  </Box>
                }
                error={
                  <Box padding="lg" textAlign="center" color="danger-500">
                    Failed to load PDF: {fileName}
                  </Box>
                }
              >
                <Page
                  pageNumber={pageNumber}
                  scale={scale}
                  onLoadSuccess={onPageLoadSuccess}
                  loading={
                    <Box padding="md" textAlign="center">
                      Loading page...
                    </Box>
                  }
                />
              </Document>
            )}

            {showBoundingBoxes && (
              <canvas
                ref={canvasRef}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  pointerEvents: 'none',
                  zIndex: 10,
                }}
              />
            )}

            {hoveredBox && (
              <Box
                style={{
                  position: 'absolute',
                  pointerEvents: 'none',
                  backgroundColor: '#ff4444',
                  color: 'white',
                  padding: '0',
                  fontSize: '12px',
                  height: `${20}px`,
                  width: `${getTooltipPosition().width}px`,
                  alignItems: 'center',
                  paddingLeft: '4px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  zIndex: 20,
                  top: getTooltipPosition().top,
                  left: getTooltipPosition().left,
                }}
              >
                {hoveredBox.fieldName}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
